import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { UserCheck, Check, X, Building2, Factory, Wrench, FileText, Eye } from "lucide-react";

const TIPO = {
  empresa: { l: "Empresa", icon: Building2, c: "bg-[#DBEAFE] text-[#142766]" },
  montadora: { l: "Montadora", icon: Factory, c: "bg-aj-amber-soft text-aj-orange-hover" },
  montador: { l: "Montador", icon: Wrench, c: "bg-[#DCFCE7] text-[#166534]" },
};

const DOC_LABELS = {
  doc_identidade: "Documento com foto",
  comprovante_residencia: "Comprovante de residência",
  selfie: "Selfie",
  cnpj_documento: "Cartão CNPJ",
  antecedentes: "Antecedentes",
};

const CICLO_LABELS = {
  cadastro_incompleto: "Cadastro Incompleto", aguardando_documentos: "Aguardando Documentos",
  em_analise: "Em Análise", pendencia_documental: "Pendência Documental",
  ativo: "Ativo", restrito: "Restrito", em_observacao: "Em Observação",
  suspenso_preventivamente: "Suspenso Preventivamente", inativo: "Inativo",
  bloqueado: "Bloqueado", encerrado: "Encerrado",
};

const DOC_STATUS = {
  enviado: { l: "Em análise", c: "bg-[#DBEAFE] text-[#142766]" },
  aprovado: { l: "Aprovado", c: "bg-[#DCFCE7] text-[#166534]" },
  rejeitado: { l: "Rejeitado", c: "bg-red-100 text-[#B91C1C]" },
};

function DocRow({ doc, onDecidir, onVer }) {
  const [motivo, setMotivo] = useState("");
  const st = DOC_STATUS[doc.status] || { l: doc.status, c: "bg-secondary" };
  return (
    <div className="flex items-center justify-between gap-2 flex-wrap py-1.5 border-b border-border/60 last:border-0"
      data-testid={`pendente-doc-${doc.doc_id}`}>
      <div className="flex items-center gap-2 min-w-0">
        <FileText size={13} className="text-aj-text-muted shrink-0" />
        <span className="text-xs font-semibold text-aj-text">{DOC_LABELS[doc.tipo] || doc.tipo}</span>
        {doc.subtipo && <span className="text-[10px] uppercase font-bold text-aj-text-muted">{doc.subtipo}</span>}
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${st.c}`}>{st.l}</span>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <Button size="sm" variant="ghost" className="h-7 text-xs px-2"
          onClick={() => onVer(doc)} data-testid={`doc-ver-${doc.doc_id}`}>
          <Eye size={12} /> Ver
        </Button>
        {doc.status === "enviado" && (
          <>
            <Input placeholder="Motivo (se rejeitar)" value={motivo}
              onChange={e => setMotivo(e.target.value)}
              className="h-7 text-xs w-40" data-testid={`doc-motivo-${doc.doc_id}`} />
            <Button size="sm" className="h-7 text-xs bg-aj-success hover:opacity-90 text-white px-2"
              onClick={() => onDecidir(doc, "aprovar", motivo)} data-testid={`doc-aprovar-${doc.doc_id}`}>
              <Check size={12} />
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs border-[#E60000] text-[#E60000] px-2"
              onClick={() => onDecidir(doc, "rejeitar", motivo)} data-testid={`doc-rejeitar-${doc.doc_id}`}>
              <X size={12} />
            </Button>
          </>
        )}
        {(doc.status === "aprovado" || doc.status === "rejeitado") && (
          <Button size="sm" variant="outline" className="h-7 text-xs px-2"
            onClick={() => onDecidir(doc, "reabrir", "")} data-testid={`doc-reabrir-${doc.doc_id}`}>
            Reanalisar
          </Button>
        )}
      </div>
    </div>
  );
}

export default function CadastrosPendentes() {
  const [docs, setDocs] = useState(null);
  const [conf, setConf] = useState([]);
  const [motivos, setMotivos] = useState({});
  const [motivosConf, setMotivosConf] = useState({});

  const load = useCallback(() => {
    api.get("/admin/cadastros-pendentes").then(r => setDocs(r.data)).catch(e => toast.error(formatApiError(e)));
    api.get("/admin/empresas-conferencia").then(r => setConf(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const decidirDocumentacao = async (e, acao) => {
    try {
      await api.post(`/admin/empresas/${e.empresa_id}/documentacao-decisao`,
        { acao, motivo: motivosConf[e.empresa_id] || null });
      toast.success(acao === "aprovar"
        ? "Documentação aprovada — empresa liberada para publicar condições"
        : "Correção solicitada — empresa avisada por WhatsApp");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const baixarDocEmpresa = async (empresaId, d) => {
    try {
      const r = await api.get(`/admin/empresas/${empresaId}/documentos/${d.id}/arquivo`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const verContratoAssinado = async (aceiteId) => {
    try {
      const r = await api.get(`/admin/contratos-assinados/${aceiteId}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const [docsMt, setDocsMt] = useState({});
  const verDocsMontadora = async (mid) => {
    if (docsMt[mid]) { setDocsMt(d => ({ ...d, [mid]: null })); return; }
    try {
      const r = await api.get(`/admin/montadoras/${mid}/documentos`);
      setDocsMt(d => ({ ...d, [mid]: r.data }));
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const baixarDocMontadora = async (mid, d) => {
    try {
      const r = await api.get(`/admin/montadoras/${mid}/documentos/${d.id}/arquivo`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const decidir = async (d, acao) => {
    try {
      await api.post(`/admin/cadastros/${d.tipo}/${d.id}/decisao`, { acao, motivo: motivos[d.id] || null });
      window.dispatchEvent(new Event("pendencias-atualizadas"));
      toast.success(acao === "aprovar"
        ? `${TIPO[d.tipo].l} liberado(a) — aviso enviado por WhatsApp`
        : "Cadastro recusado — aviso enviado por WhatsApp");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const decidirDoc = async (doc, acao, motivo) => {
    try {
      await api.post(`/admin/documentos-montador/${doc.doc_id}/decisao`, { acao, motivo: motivo || null });
      toast.success(acao === "aprovar" ? "Documento aprovado" : acao === "reabrir" ? "Documento reaberto para análise" : "Documento rejeitado — montador avisado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const verDoc = async (doc) => {
    try {
      const r = await api.get(`/documentos-montador/${doc.doc_id}/arquivo`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="cadastros-pendentes-page">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">Administração</div>
        <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-aj-blue-deep mt-1">Cadastros Pendentes</h1>
        <p className="text-sm text-aj-text-muted mt-1 max-w-2xl">
          Empresas, Montadoras e Montadores só operam após sua liberação. Analise os documentos do Montador antes de liberar. Aprovar envia aviso automático por WhatsApp.
        </p>
      </div>

      <div className="space-y-3">
        {docs === null && <div className="aj-card p-10 text-center text-aj-text-muted">Carregando…</div>}
        {docs?.length === 0 && (
          <div className="aj-card p-10 text-center text-aj-text-muted" data-testid="pendentes-vazio">
            <UserCheck className="w-8 h-8 mx-auto mb-2 opacity-40" />
            Nenhum cadastro aguardando liberação. Tudo em dia!
          </div>
        )}
        {docs?.map(d => {
          const t = TIPO[d.tipo];
          const Icon = t.icon;
          return (
            <div key={`${d.tipo}-${d.id}`} className="aj-card p-5 border-l-4 border-l-[#FF6700]"
              data-testid={`pendente-card-${d.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Icon size={16} className="text-aj-orange" />
                    <span className="font-display font-bold text-aj-blue-deep">{d.nome || "—"}</span>
                    <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${t.c}`}>{t.l}</span>
                    {d.status_ciclo && (
                      <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase bg-[#F4F7FB] text-aj-text-muted"
                        data-testid={`pendente-ciclo-${d.id}`}>
                        {CICLO_LABELS[d.status_ciclo] || d.status_ciclo.replace(/_/g, " ")}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-aj-text-muted mt-1.5 space-x-3">
                    {d.documento && <span>Doc: <b>{d.documento}</b></span>}
                    {d.cidade && <span>{d.cidade}/{d.estado || ""}</span>}
                    {d.telefone && <span>📱 {d.telefone}</span>}
                  </div>
                  <div className="text-xs text-aj-text-muted mt-0.5">
                    {d.email} · cadastrado em {d.created_at ? new Date(d.created_at).toLocaleString("pt-BR") : "—"}
                  </div>
                  {d.tipo === "montadora" && (
                    <div className="mt-3">
                      <Button size="sm" variant="outline" className="h-7 text-xs"
                        onClick={() => verDocsMontadora(d.id)} data-testid={`mt-docs-btn-${d.id}`}>
                        <FileText size={12} /> {docsMt[d.id] ? "Ocultar documentos" : "Ver documentos da empresa"}
                      </Button>
                      {docsMt[d.id] && (
                        <div className="mt-2 bg-[#F4F7FB] rounded-lg p-3 space-y-1" data-testid={`mt-docs-${d.id}`}>
                          {docsMt[d.id].length === 0 && <div className="text-xs text-aj-text-muted">Nenhum documento enviado.</div>}
                          {docsMt[d.id].map(doc => (
                            <div key={doc.id} className="flex items-center justify-between text-xs">
                              <span><b>{{ cartao_cnpj: "Cartão CNPJ", contrato_social: "Contrato Social",
                                comprovante_bancario: "Comprovante bancário", documento_socio: "Documento de sócio" }[doc.tipo] || doc.tipo}</b> · {doc.nome_original}</span>
                              <Button size="sm" variant="ghost" className="h-6 px-2"
                                onClick={() => baixarDocMontadora(d.id, doc)} data-testid={`mt-doc-ver-${doc.id}`}>
                                <Eye size={12} /> Ver
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {d.tipo === "montador" && (
                    <div className="mt-3 bg-[#F4F7FB] rounded-lg p-3" data-testid={`pendente-docs-${d.id}`}>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-aj-text-muted mb-1">
                        Documentos ({(d.documentos || []).length} enviado(s) · obrigatórios: {(d.docs_obrigatorios || []).map(x => DOC_LABELS[x] || x).join(", ")})
                      </div>
                      {(d.documentos || []).length === 0 && (
                        <div className="text-xs text-aj-text-muted">Nenhum documento enviado ainda.</div>
                      )}
                      {(d.documentos || []).map(doc => (
                        <DocRow key={doc.doc_id} doc={doc} onDecidir={decidirDoc} onVer={verDoc} />
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2 w-full sm:w-72">
                  <Input placeholder="Motivo (opcional, vai no WhatsApp se recusar)"
                    value={motivos[d.id] || ""}
                    onChange={e => setMotivos(m => ({ ...m, [d.id]: e.target.value }))}
                    data-testid={`pendente-motivo-${d.id}`} />
                  <div className="flex gap-2">
                    <Button onClick={() => decidir(d, "aprovar")}
                      className="flex-1 bg-aj-success hover:opacity-90 text-white"
                      data-testid={`pendente-aprovar-${d.id}`}>
                      <Check size={15} /> Liberar
                    </Button>
                    <Button onClick={() => decidir(d, "recusar")} variant="outline"
                      className="flex-1 border-[#E60000] text-[#E60000] hover:bg-red-50"
                      data-testid={`pendente-recusar-${d.id}`}>
                      <X size={15} /> Recusar
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {conf.length > 0 && (
        <div className="space-y-3" data-testid="conferencia-documental">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">Empresas</div>
            <h2 className="font-display font-extrabold text-xl text-aj-blue-deep mt-1">Conferência Documental</h2>
            <p className="text-xs text-aj-text-muted mt-1 max-w-2xl">
              Empresas que assinaram o Contrato MontaJá e aguardam conferência do Contrato Social e do representante.
              Elas já podem configurar a operação; a publicação para a rede só libera após sua aprovação.
            </p>
          </div>
          {conf.map(e => (
            <div key={e.empresa_id} className={`aj-card p-5 border-l-4 ${e.documentacao_status === "correcao_necessaria" ? "border-l-red-500" : "border-l-[#142766]"}`}
              data-testid={`conf-card-${e.empresa_id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Building2 size={16} className="text-[#142766]" />
                    <span className="font-display font-bold text-aj-blue-deep">{e.nome}</span>
                    <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase bg-[#DBEAFE] text-[#142766]">
                      {e.documentacao_status === "correcao_necessaria" ? "Aguardando correção" : "Em conferência"}
                    </span>
                  </div>
                  <div className="text-xs text-aj-text-muted mt-1.5 space-x-3">
                    <span>CNPJ: <b>{e.cnpj}</b></span>
                    {e.contrato_montaja === "assinado" && <span className="text-emerald-700 font-semibold">✓ Contrato MontaJá assinado {e.contrato_montaja_em && `em ${new Date(e.contrato_montaja_em).toLocaleString("pt-BR")}`}</span>}
                  </div>
                  {e.representante && (
                    <div className="text-xs mt-1.5 bg-[#F4F7FB] rounded-lg p-2" data-testid={`conf-rep-${e.empresa_id}`}>
                      <b>Representante:</b> {e.representante.nome} · CPF {e.representante.cpf} · {e.representante.cargo?.replace(/_/g, "-")} · {e.representante.email} · 📱 {e.representante.telefone}
                    </div>
                  )}
                  {e.documentacao_motivo && (
                    <div className="text-xs mt-1 text-red-600">Motivo da correção: {e.documentacao_motivo}</div>
                  )}
                  <div className="mt-2 flex gap-2 flex-wrap">
                    {(e.documentos || []).map(d => (
                      <Button key={d.id} size="sm" variant="outline" className="h-7 text-xs"
                        onClick={() => baixarDocEmpresa(e.empresa_id, d)} data-testid={`conf-doc-${d.id}`}>
                        <FileText size={12} /> Contrato Social v{d.versao} ({d.status})
                      </Button>
                    ))}
                    {e.aceite_id && (
                      <Button size="sm" variant="outline" className="h-7 text-xs"
                        onClick={() => verContratoAssinado(e.aceite_id)} data-testid={`conf-pdf-${e.empresa_id}`}>
                        <Eye size={12} /> Contrato MontaJá assinado (PDF)
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-2 w-full sm:w-72">
                  <Input placeholder="Motivo (obrigatório para solicitar correção)"
                    value={motivosConf[e.empresa_id] || ""}
                    onChange={ev => setMotivosConf(m => ({ ...m, [e.empresa_id]: ev.target.value }))}
                    data-testid={`conf-motivo-${e.empresa_id}`} />
                  <div className="flex gap-2">
                    <Button onClick={() => decidirDocumentacao(e, "aprovar")}
                      className="flex-1 bg-aj-success hover:opacity-90 text-white"
                      data-testid={`conf-aprovar-${e.empresa_id}`}>
                      <Check size={15} /> Aprovar
                    </Button>
                    <Button onClick={() => decidirDocumentacao(e, "solicitar_correcao")} variant="outline"
                      className="flex-1 border-[#E60000] text-[#E60000] hover:bg-red-50"
                      data-testid={`conf-correcao-${e.empresa_id}`}>
                      <X size={15} /> Corrigir
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
